import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";

/**
 * PullToRefresh — Instagram-style global pull-down-to-refresh wrapper.
 *
 * Applied at the app root so any page can be refreshed by dragging down
 * from the top. The gesture only activates when the page is scrolled to
 * the very top (window.scrollY === 0), so it never interferes with normal
 * scrolling in the middle of a long page.
 *
 * On release past the trigger threshold, we call the provided `onRefresh`
 * callback (default: reload the current route via a soft React reload
 * event). We fall back to `window.location.reload()` if no listener picks
 * up the event within 300ms.
 */
const TRIGGER_DISTANCE = 70;   // px — user must drag at least this far to trigger
const MAX_PULL = 110;          // px — visual cap on the pull indicator
const DAMPING = 0.55;          // slow the visual movement to feel elastic

export default function PullToRefresh({ children, onRefresh }) {
  const [pull, setPull] = useState(0);       // current visible pull distance
  const [state, setState] = useState("idle"); // idle | pulling | refreshing
  const startY = useRef(0);
  const active = useRef(false);
  const stateRef = useRef("idle");

  useEffect(() => { stateRef.current = state; }, [state]);

  const doRefresh = useCallback(async () => {
    setState("refreshing");
    // Give the spinner a moment to appear, then reload the current page.
    // Pages that want to intercept can call `e.preventDefault()` on the
    // `app:refresh` event to opt out of the full reload.
    setTimeout(() => {
      const evt = new CustomEvent("app:refresh", { cancelable: true });
      const notHandled = window.dispatchEvent(evt);
      if (onRefresh) {
        try { onRefresh(); } catch { /* ignore */ }
        setTimeout(() => { setState("idle"); setPull(0); }, 700);
        return;
      }
      if (notHandled) {
        // No listener consumed it → do a full page reload
        window.location.reload();
      } else {
        // A listener handled it → just reset the UI
        setTimeout(() => { setState("idle"); setPull(0); }, 700);
      }
    }, 200);
  }, [onRefresh]);

  useEffect(() => {
    const onTouchStart = (e) => {
      // Only start tracking if we're at the very top of the page
      if (window.scrollY > 2) { active.current = false; return; }
      // Disable pull-to-refresh entirely inside the games route (it's a game surface).
      if (/^\/(games|hood-chain)/.test(window.location.pathname)) { active.current = false; return; }
      // Skip if inside a horizontal scroller or a modal
      if (e.target.closest("[data-no-pull-refresh]")) { active.current = false; return; }
      if (stateRef.current === "refreshing") { active.current = false; return; }
      active.current = true;
      startY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (!active.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setPull(0); return; }
      // Damped visual distance so it feels elastic
      const damped = Math.min(dy * DAMPING, MAX_PULL);
      setPull(damped);
      if (damped > 20) setState("pulling");
      // Prevent the native browser rubber-band while we're pulling
      if (window.scrollY < 1 && dy > 6) {
        try { e.preventDefault(); } catch { /* passive listener may throw */ }
      }
    };

    const onTouchEnd = () => {
      if (!active.current) return;
      active.current = false;
      if (stateRef.current === "refreshing") return;
      if (pull >= TRIGGER_DISTANCE) {
        doRefresh();
      } else {
        setState("idle");
        setPull(0);
      }
    };

    // Use non-passive so we can preventDefault inside touchmove
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pull, doRefresh]);

  const refreshing = state === "refreshing";
  const showIndicator = pull > 6 || refreshing;
  const progress = Math.min(pull / TRIGGER_DISTANCE, 1);

  return (
    <>
      {/* Pull indicator — fixed overlay that appears above the content */}
      <div
        aria-hidden={!showIndicator}
        className="fixed top-0 inset-x-0 z-[9998] flex items-start justify-center pointer-events-none"
        style={{
          height: `${Math.max(pull, refreshing ? 60 : 0)}px`,
          transition: refreshing || pull === 0 ? "height 260ms ease" : "none",
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className="mt-3 size-9 rounded-full bg-primary/95 shadow-lg backdrop-blur flex items-center justify-center"
          style={{
            opacity: showIndicator ? 1 : 0,
            transform: `scale(${refreshing ? 1 : Math.max(0.6, progress)})`,
            transition: refreshing ? "opacity 200ms ease" : "transform 120ms ease",
          }}
        >
          <RefreshCw
            className={`size-4 text-white ${refreshing ? "animate-spin" : ""}`}
            style={{ transform: refreshing ? "none" : `rotate(${progress * 270}deg)`, transition: "transform 60ms linear" }}
          />
        </div>
      </div>
      {/* Content — visually pushed down while pulling for a native feel */}
      <div
        style={{
          transform: `translateY(${Math.min(pull * 0.6, 60)}px)`,
          transition: refreshing || pull === 0 ? "transform 260ms ease" : "none",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </>
  );
}
